import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import spaceImage from '../images/space.webp';
import { authApi } from '../services/api';
import { useRecaptcha } from '../hooks/useRecaptcha';
import Spinner from './Spinner';
import TermsAndPrivacy from './TermsAndPrivacy';
import { validateUsername } from '../utils/validation';

const CompleteSignup: React.FC = () => {
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [signupToken, setSignupToken] = useState<string | null>(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isLoaded: recaptchaLoaded, getToken } = useRecaptcha();

    // Extract signupToken from URL
    useEffect(() => {
        const token = searchParams.get('signupToken');
        if (token) {
            setSignupToken(token);
        } else {
            setError('Invalid or missing signup token. Please check your email link.');
        }
    }, [searchParams]);

    const validateForm = (): string | null => {
        // Validate username using shared validation function
        const usernameValidation = validateUsername(userName);
        if (!usernameValidation.isValid) {
            return usernameValidation.error || 'Invalid username';
        }
        if (!password) {
            return 'Password is required';
        }
        if (password.length < 8) {
            return 'Password must be at least 8 characters long';
        }
        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validate form
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (!signupToken) {
            setError('Invalid or missing signup token. Please check your email link.');
            return;
        }

        setLoading(true);

        try {
            // Get reCAPTCHA token
            const recaptchaToken = await getToken('complete_signup');

            const response = await authApi.completeSignup({
                token: signupToken,
                userName: userName.trim(),
                password,
                confirmPassword,
                recaptchaToken,
            });

            if (response.status === 'success') {
                // Successful signup completion
                console.log('Signup completed successfully:', response);
                setShowSuccessDialog(true);
            } else {
                setError(response.message || 'Failed to complete signup');
            }
        } catch (err: any) {
            console.error('Complete signup error:', err);
            if (err.message && err.message.includes('reCAPTCHA')) {
                setError('reCAPTCHA verification failed. Please try again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!recaptchaLoaded) {
        return (
            <div
                className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
                style={{
                    backgroundImage: `url(${spaceImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className='text-center'>
                    <Spinner size='lg' color='blue' />
                    <p className='mt-4 text-white'>Loading security verification...</p>
                </div>
            </div>
        );
    }

    // Success Dialog Component
    const SuccessDialog = () => (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4'>
            <div className='bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center'>
                {/* Success Icon */}
                <div className='mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6'>
                    <svg
                        className='h-8 w-8 text-green-600'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M5 13l4 4L19 7'
                        />
                    </svg>
                </div>

                {/* Success Message */}
                <h3 className='text-2xl font-bold text-gray-900 mb-4'>Welcome to Puzzle Planet!</h3>
                <p className='text-gray-600 mb-8'>
                    Your account has been successfully created. You can now sign in to start
                    playing!
                </p>

                {/* Action Button */}
                <button
                    onClick={() => navigate('/login')}
                    className='w-full bg-gradient-to-r from-blue-600 to-purple-600 !text-black font-semibold py-3 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all duration-200'
                >
                    Go to Sign In
                </button>
            </div>
        </div>
    );

    return (
        <>
            {showSuccessDialog && <SuccessDialog />}
            <div
                className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
                style={{
                    backgroundImage: `url(${spaceImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className='max-w-md w-full space-y-8'>
                    {/* Header */}
                    <div className='text-center'>
                        <h1 className='text-4xl font-bold text-orange-500 mb-2'>
                            Complete Your Signup
                        </h1>
                        <p className='text-lg text-white'>
                            Welcome to Puzzle Planet! Please complete your account setup.
                        </p>
                    </div>

                    {/* Complete Signup Form */}
                    <div className='bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100'>
                        <form onSubmit={handleSubmit} className='space-y-6'>
                            {/* Username Field */}
                            <div>
                                <label
                                    htmlFor='userName'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Username
                                </label>
                                <input
                                    type='text'
                                    id='userName'
                                    value={userName}
                                    onChange={e => setUserName(e.target.value)}
                                    required
                                    disabled={loading}
                                    autoComplete='off'
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Choose a username'
                                    minLength={3}
                                />
                                <p className='mt-2 text-sm text-gray-500'>
                                    Allowed characters: letters, numbers, underscores (_), and
                                    hyphens (-)
                                </p>
                            </div>

                            {/* Password Field */}
                            <div>
                                <label
                                    htmlFor='password'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Password
                                </label>
                                <input
                                    type='password'
                                    id='password'
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    autoComplete='new-password'
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Create a password'
                                    minLength={8}
                                />
                            </div>

                            {/* Confirm Password Field */}
                            <div>
                                <label
                                    htmlFor='confirmPassword'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Confirm Password
                                </label>
                                <input
                                    type='password'
                                    id='confirmPassword'
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    autoComplete='new-password'
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Confirm your password'
                                    minLength={8}
                                />
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                                    {error}
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type='submit'
                                disabled={loading || !signupToken}
                                className='w-full bg-gradient-to-r from-blue-600 to-purple-600 !text-black font-semibold py-3 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                            >
                                {loading ? (
                                    <div className='flex items-center justify-center'>
                                        <Spinner size='md' color='black' className='-ml-1 mr-3' />
                                        Completing signup...
                                    </div>
                                ) : (
                                    'Complete Signup'
                                )}
                            </button>
                            <TermsAndPrivacy />
                        </form>
                    </div>

                    {/* Footer */}
                    <div className='text-center'>
                        <p className='text-sm text-gray-500'>
                            Already have an account?{' '}
                            <button
                                type='button'
                                onClick={() => navigate('/login')}
                                className='text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200'
                            >
                                Sign in here
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CompleteSignup;
